import { AtSign, ExternalLink, Link2 } from "lucide-react";
import type { ContactFacts } from "../../../../lib/siteScan";
import { PhoneLink } from "../../../telephony/PhoneLink";
import type { T } from "../types";
import { CopyButton, Field, Panel } from "./primitives";

/** Everything on the page that can be written to, called or clicked next. */
export function ContactsPanel({
  contacts,
  companyName,
  regCode,
  domain,
  t,
}: {
  contacts: ContactFacts;
  companyName?: string;
  regCode?: string;
  domain?: string;
  t: T;
}) {
  const empty =
    !contacts.emails.length && !contacts.phones.length && !contacts.social.length && !contacts.keyPages.length;

  return (
    <Panel icon={AtSign} title={t("scanner.contacts.title")}>
      {empty ? (
        <p className="text-sm text-muted-foreground">{t("scanner.contacts.empty")}</p>
      ) : (
        <div className="space-y-3">
          {contacts.emails.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-medium text-muted-foreground">{t("scanner.contacts.emails")}</p>
              <ul className="space-y-1">
                {contacts.emails.map((email) => (
                  <li key={email} className="flex items-center gap-1">
                    {/* `min-w-0` next to `truncate`: an address has nothing to
                        break at, and a flex item without it refuses to go below
                        its own min-content - so the ellipsis never drew and a
                        long address ran out past the panel instead. */}
                    <a href={`mailto:${email}`} className="min-w-0 truncate text-sm text-primary hover:underline">
                      {email}
                    </a>
                    <CopyButton value={email} label={t("common.copy")} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {contacts.phones.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-medium text-muted-foreground">{t("scanner.contacts.phones")}</p>
              <ul className="space-y-1">
                {contacts.phones.map((phone) => (
                  <li key={phone} className="flex items-center gap-1">
                    <PhoneLink
                      phone={phone}
                      source="scanner"
                      companyName={companyName}
                      regCode={regCode}
                      leadDomain={domain}
                    />
                    <CopyButton value={phone} label={t("common.copy")} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(contacts.regCodes.length > 0 || contacts.vatNumbers.length > 0) && (
            <dl className="grid grid-cols-2 gap-3 border-t border-[var(--crm-hair)] pt-3">
              <Field label={t("scanner.contacts.regCode")} value={contacts.regCodes.join(", ")} mono />
              <Field label={t("scanner.contacts.vat")} value={contacts.vatNumbers.join(", ")} mono />
            </dl>
          )}

          {contacts.social.length > 0 && (
            <div className="border-t border-[var(--crm-hair)] pt-3">
              <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">{t("scanner.contacts.social")}</p>
              <div className="flex flex-wrap gap-1.5">
                {contacts.social.map((s) => (
                  <a
                    key={s.network}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--crm-sunk)] px-2.5 py-1 text-xs capitalize transition-shadow hover:shadow-[var(--crm-sh-sm)]"
                  >
                    {s.network} <ExternalLink className="size-3" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {contacts.keyPages.length > 0 && (
            <div className="border-t border-[var(--crm-hair)] pt-3">
              <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">{t("scanner.contacts.pages")}</p>
              <div className="flex flex-wrap gap-1.5">
                {contacts.keyPages.map((p) => (
                  <a
                    key={p.kind}
                    href={p.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--crm-sunk)] px-2.5 py-1 text-xs transition-shadow hover:shadow-[var(--crm-sh-sm)]"
                  >
                    <Link2 className="size-3" /> {t(`scanner.page.${p.kind}`)}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}
